import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Sparkles, Zap, Palette, Camera, Plane, Briefcase, Heart, ChevronDown, Mail, Twitter, Instagram, Linkedin } from 'lucide-react'
import { Logo } from '../components/Logo'

export function LandingPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index)
  }

  const faqs = [
    {
      question: "Is it really free?",
      answer: "Yes! Completely free. No hidden costs, no credit card required, no catch."
    },
    {
      question: "Will it sound generic?",
      answer: "Nope! You get captions with different moods (Calm, Bold, Romantic, etc.) plus your custom keywords. Each caption has its own personality."
    },
    {
      question: "Do I need to sign up?",
      answer: "Not at all. Just click 'Try It Now' and start generating captions immediately."
    },
    {
      question: "How many captions do I get?",
      answer: "You get multiple unique captions per photo, each with a different vibe and style."
    },
    {
      question: "Can I use these captions on any platform?",
      answer: "Absolutely! Instagram, Facebook, Pinterest, Twitter - wherever you post photos."
    },
    {
      question: "What if I don't like the captions?",
      answer: "Just hit regenerate! You can keep generating until you find the perfect one."
    }
  ]

  return (
    <div className="min-h-screen landing-gradient">
      <Helmet>
        <title>The Caption Studio - AI Instagram Caption Generator</title>
        <meta name="description" content="Upload your photo, pick a mood, and get AI-generated Instagram captions that stop the scroll. Lowkey, Aesthetic, Unhinged and more — free to try." />
        <link rel="canonical" href="https://thecaption.studio/" />
      </Helmet>
      {/* Ambient Light Circles */}
      <div style={{
        position: 'fixed',
        top: '20%',
        left: '-10%',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124, 58, 237, 0.10) 0%, transparent 70%)',
        filter: 'blur(80px)',
        pointerEvents: 'none',
        zIndex: 0,
        willChange: 'transform',
        animation: 'breatheDrift 25s ease-in-out infinite'
      }} />
      <div style={{
        position: 'fixed',
        top: '30%',
        right: '-10%',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(224, 64, 251, 0.10) 0%, transparent 70%)',
        filter: 'blur(80px)',
        pointerEvents: 'none',
        zIndex: 0,
        willChange: 'transform',
        animation: 'breatheDrift 25s ease-in-out 5s infinite'
      }} />
      {/* Header */}
      <header className="sticky-header px-6 py-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50 }}>
        <nav className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex flex-col gap-0 pl-4">
            <Logo />
          </div>
          <div className="flex items-center pr-2 gap-3">
            <Link to="/about" className="nav-link" style={{ fontSize: '0.78rem' }}>About</Link>
            <Link to="/blog" className="nav-link" style={{ fontSize: '0.78rem' }}>Blog</Link>
            <Link to="/how-it-works" className="nav-link" style={{ fontSize: '0.78rem' }}>How It Works</Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-6 py-20 pt-32">
        <div className="text-center relative">
          {/* Background gradient blob */}
          <div className="gradient-blob" style={{ top: '-200px', left: '50%', transform: 'translateX(-50%)' }}></div>
          
          {/* Badge above headline */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in"
            style={{
              fontFamily: "'Space Grotesk', 'Inter', sans-serif",
              color: '#ffffff',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(224, 64, 251, 0.7)',
              boxShadow: '0 0 15px rgba(255, 0, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              transition: 'transform 0.25s ease, box-shadow 0.25s ease'
            }}
          >
            <Sparkles className="h-4 w-4" style={{ color: '#e040fb' }} />
            <span>Free • No Sign-Up Required</span>
          </div>
          
          <h1 className="hero-title text-6xl font-bold mb-4 max-w-2xl mx-auto leading-tight" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
            <span className="block text-white">Post in <span style={{ filter: 'drop-shadow(0 0 15px rgba(142, 143, 250, 0.4))' }}><span style={{
              background: 'linear-gradient(135deg, #D1D1F7 0%, #8E8FFA 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>30 Seconds</span></span>,</span>
            <span className="block text-white">Not 30 Minutes</span>
          </h1>

          {/* Tagline */}
          <p className="mb-8 mx-auto" style={{
            fontFamily: "'Space Grotesk', 'Inter', sans-serif",
            fontSize: '19px',
            letterSpacing: '1px',
            maxWidth: '480px'
          }}>
            <span style={{
              background: 'linear-gradient(90deg, #ffffff 0%, #a0a0a0 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Input the vibe</span>
            <span style={{
              color: '#e040fb',
              textShadow: '0 0 8px rgba(224, 64, 251, 0.9), 0 0 16px rgba(212, 0, 255, 0.5)',
              margin: '0 8px'
            }}>·</span>
            <span style={{
              background: 'linear-gradient(90deg, #ffffff 0%, #a0a0a0 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Let AI cook</span>
            <span style={{
              color: '#e040fb',
              textShadow: '0 0 8px rgba(224, 64, 251, 0.9), 0 0 16px rgba(212, 0, 255, 0.5)',
              margin: '0 8px'
            }}>·</span>
            <span style={{
              background: 'linear-gradient(90deg, #ffffff 0%, #a0a0a0 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Go viral</span>
          </p>

          {/* Urgency/FOMO */}
          <p className="text-sm text-gray-500 mb-6">
            Join 1,000+ creators who stopped overthinking captions
          </p>

          <div className="flex justify-center mb-12">
            <Link to="/generate" className="btn-cta-pill">
              Try It Now
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>

          {/* Before/After Examples */}
          <div className="mb-8">
            <p className="text-lg font-medium text-gray-300 mb-6" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>Stop settling for boring captions</p>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { before: '"At the beach today 🏖️"', after: '"Salt in my hair, sun on my skin, not a single worry in sight"' },
                { before: '"Coffee time ☕"', after: '"A quiet coffee, a loud main character moment"' },
                { before: '"Sunset vibes 🌅"', after: '"Chasing golden hours and catching feelings"' }
              ].map((ex, i) => (
                <div key={i} className="text-left" style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 100%)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '20px',
                  padding: '32px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.3), 0 0 20px rgba(224,64,251,0.08)'
                }}>
                  <span className="card-label card-label-before">❌ Before</span>
                  <div className="text-sm mb-4 italic" style={{ color: 'rgba(255,255,255,0.45)' }}>{ex.before}</div>
                  <div className="my-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}></div>
                  <span className="card-label card-label-after">✨ After</span>
                  <div className="text-base font-medium leading-relaxed" style={{ color: '#ffffff' }}>{ex.after}</div>
                </div>
              ))}
            </div>
          </div>


          {/* Feature Cards - Bento Grid */}
          <div className="grid md:grid-cols-3 gap-6 mt-20 max-w-5xl mx-auto">
            {/* Wide card */}
            <div className="feature-card md:col-span-2 text-left" style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(224,64,251,0.12)', border: '1px solid rgba(224,64,251,0.2)' }}>
                <Zap className="h-5 w-5" style={{ color: '#e040fb' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#ffffff', fontFamily: "'Space Grotesk','Inter',sans-serif" }}>Never miss the golden hour posting window again</h3>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Upload your image, add keywords and mood, get perfect captions instantly.</p>
            </div>

            {/* Tall card */}
            <div className="feature-card text-left" style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.2)' }}>
                <Palette className="h-5 w-5" style={{ color: '#7c3aed' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#ffffff', fontFamily: "'Space Grotesk','Inter',sans-serif" }}>One photo. Many personalities.</h3>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>From aesthetic minimal to main character energy - each style matches your vibe.</p>
            </div>

            {/* Full width card */}
            <div className="feature-card md:col-span-3 text-left" style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(224,64,251,0.12)', border: '1px solid rgba(224,64,251,0.2)' }}>
                <Sparkles className="h-5 w-5" style={{ color: '#e040fb' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#ffffff', fontFamily: "'Space Grotesk','Inter',sans-serif" }}>No more generic 'living my best life' captions</h3>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Smart context interpretation that creates captions based on your image and inputs.</p>
            </div>
          </div>

          {/* Testimonials - Bento Grid */}
          <div className="mt-16 mb-16">
            <p className="text-lg font-medium text-gray-300 mb-6" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}>What creators are saying</p>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { quote: "Finally, captions that don't sound like a robot wrote them!", name: "Sarah M.", role: "Travel Blogger", span: "" },
                { quote: "I used to spend 20 minutes per caption. Now it takes 30 seconds.", name: "Priya K.", role: "Lifestyle Influencer", span: "" },
                { quote: "The mood options are perfect. Each caption actually matches my vibe.", name: "Alex R.", role: "Content Creator", span: "" }
              ].map((t, i) => (
                <div key={i} className="transition-all duration-300" style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 100%)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '24px',
                  padding: '32px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  cursor: 'default'
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.border = '1px solid rgba(224,64,251,0.5)'
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.3), 0 0 20px rgba(224,64,251,0.2)'
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.border = '1px solid rgba(255,255,255,0.15)'
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.3)'
                }}
                >
                  <div className="italic mb-4 text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>"{t.quote}"</div>
                  <div className="text-sm font-semibold" style={{ color: '#ffffff', fontFamily: "'Space Grotesk','Inter',sans-serif" }}>{t.name}</div>
                  <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{t.role}</div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-20 max-w-3xl mx-auto">
            <h3 className="text-2xl font-semibold text-white mb-8 text-center" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>Frequently Asked Questions</h3>
            <div>
              {faqs.map((faq, index) => (
                <div key={index} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full py-5 text-left flex items-center justify-between transition-all duration-200"
                    style={{ background: 'transparent', border: 'none', outline: 'none' }}
                  >
                    <span className="font-semibold text-white pr-4" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}>{faq.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'transform rotate-180' : ''}`}
                      style={{ color: openFaqIndex === index ? '#e040fb' : 'rgba(255,255,255,0.4)' }}
                    />
                  </button>
                  {openFaqIndex === index && (
                    <div className="pb-5 text-left animate-fade-in text-sm leading-relaxed" style={{ color: '#A1A1A1' }}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Category Cards - Bento Grid */}
          <div className="mt-20">
            <h3 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}>Perfect for every moment</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {[
                { icon: <Camera className="h-7 w-7" />, title: 'Selfie captions', sub: "that aren't cringe", col: '' },
                { icon: <Plane className="h-7 w-7" />, title: 'Travel posts', sub: 'that tell a story', col: '' },
                { icon: <Briefcase className="h-7 w-7" />, title: 'Professional content', sub: 'that converts', col: '' },
                { icon: <Heart className="h-7 w-7" />, title: 'Couple photos', sub: 'with the right feels', col: '' },
              ].map((item, i) => (
                <div key={i} className="p-6 text-center transition-all duration-300" style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 100%)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '24px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.border = '1px solid rgba(224,64,251,0.4)'
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.3), 0 0 16px rgba(224,64,251,0.15)'
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.border = '1px solid rgba(255,255,255,0.15)'
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.3)'
                }}
                >
                  <div className="flex justify-center mb-3" style={{ color: '#e040fb' }}>{item.icon}</div>
                  <div className="font-semibold text-white text-sm mb-1" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}>{item.title}</div>
                  <div className="text-xs text-gray-500">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-20 text-center">
            <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}>Ready to stop overthinking captions?</h3>
            <p className="text-gray-400 mb-8">Try it now - no sign up required</p>
            <Link to="/generate" className="btn-cta-pill">
              Try It Now
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ background: '#080808', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '5rem' }}>
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand Column */}
            <div className="md:col-span-2">
              <Logo showText={false} />
              <p className="text-sm mt-4 mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Input the vibe · Let AI cook · Go viral
              </p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                Made with ❤️ in India
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4 text-white" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}>Quick Links</h4>
              <ul className="space-y-2">
                {[
                  { to: '/generate', label: 'Generator' },
                  { to: '/about', label: 'About' },
                  { to: '/blog', label: 'Blog' },
                  { to: '/privacy', label: 'Privacy Policy' },
                  { to: '/terms', label: 'Terms of Service' },
                ].map(link => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm transition-all duration-200"
                      style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}
                      onMouseEnter={e => {
                        e.currentTarget.style.color = '#ffffff'
                        e.currentTarget.style.textDecoration = 'underline'
                        e.currentTarget.style.textDecorationColor = '#e040fb'
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.color = 'rgba(255,255,255,0.6)'
                        e.currentTarget.style.textDecoration = 'none'
                      }}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="font-semibold mb-4 text-white" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}>Connect</h4>
              <div className="space-y-3">
                <a
                  href="mailto:hello.captiostudio@gmail.com"
                  className="flex items-center text-sm transition-all duration-200"
                  style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = '#ffffff'
                    e.currentTarget.style.textDecoration = 'underline'
                    e.currentTarget.style.textDecorationColor = '#e040fb'
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = 'rgba(255,255,255,0.6)'
                    e.currentTarget.style.textDecoration = 'none'
                  }}
                >
                  <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                  hello.captiostudio@gmail.com
                </a>
                <div className="flex space-x-4 mt-4">
                  {[
                    { icon: <Twitter className="h-5 w-5" />, label: 'Twitter' },
                    { icon: <Instagram className="h-5 w-5" />, label: 'Instagram' },
                    { icon: <Linkedin className="h-5 w-5" />, label: 'LinkedIn' },
                  ].map(s => (
                    <a
                      key={s.label}
                      href="#"
                      aria-label={s.label}
                      className="transition-all duration-200"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#e040fb')}
                      onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
              © {new Date().getFullYear()} The Caption Studio. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}